import { captureWalletEvent } from "@shared/analytics/walletEvents";
import { walletJourney } from "@shared/analytics/walletJourney";
import type { PublishedTermsDescriptor } from "@shared/types/yaas";
import {
    ArrowRightLongIcon,
    CheckboxIcon,
    CheckIcon,
    CrossIcon
} from "@solomon-labs/design/components/icons";
import {
    Button,
    ButtonIcon,
    Dialog,
    DialogClose,
    DialogContent,
    DialogTitle,
    Text
} from "@solomon-labs/design/components/ui";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import { solomonMainAppTerms } from "@/content/terms/solomonMainAppTerms";
import type {
    EnrollmentEvent,
    EnrollmentJourney,
    EnrollmentProperties
} from "@/services/analytics/enrollment";

import { SolomonTermsDocument } from "./SolomonTermsDocument";

export type TermsSurface = "main_app" | "retail_enrollment";

export type TermsTrigger =
    | "first_visit"
    | "retail_enrollment_form"
    | "modal_showcase";

const defaultTermsAnalyticsProperties = Object.freeze({
    terms_document_key: solomonMainAppTerms.documentKey,
    terms_version: solomonMainAppTerms.version,
    terms_content_digest: solomonMainAppTerms.contentDigest
});

interface TermsOfServiceModalProps {
    analytics?: EnrollmentJourney;
    document?: PublishedTermsDescriptor;
    open: boolean;
    onClose: () => void;
    onAccept: () => void;
    surface: TermsSurface;
    trigger: TermsTrigger;
}

export function TermsOfServiceModal({
    open,
    onClose,
    onAccept,
    surface,
    trigger,
    document,
    analytics
}: TermsOfServiceModalProps): React.ReactElement | null {
    const captureTerms = useCallback(
        (
            event: Extract<EnrollmentEvent, `terms:${string}`>,
            properties: Pick<
                EnrollmentProperties,
                | "surface"
                | "trigger"
                | "terms_document_key"
                | "terms_version"
                | "terms_content_digest"
                | "checked"
                | "had_checked_box"
                | "acceptance_latency_ms"
                | "reason"
            >
        ): void => {
            if (analytics)
                analytics.send(event, { ...properties, control: "modal" });
            else
                captureWalletEvent(event, {
                    ...walletJourney.context(),
                    ...properties
                });
        },
        [analytics]
    );
    const termsAnalyticsProperties = useMemo(
        () =>
            document
                ? {
                      terms_document_key: document.documentKey,
                      terms_version: document.version,
                      terms_content_digest: document.contentDigest
                  }
                : defaultTermsAnalyticsProperties,
        [document]
    );
    const [termsChecked, setTermsChecked] = useState<boolean>(false);
    const openedAtRef = useRef<number | null>(null);
    const lastCheckedRef = useRef<boolean>(false);

    useEffect(() => {
        if (!open) {
            setTermsChecked(false);
            openedAtRef.current = null;
            lastCheckedRef.current = false;
            return;
        }

        if (openedAtRef.current !== null) return;
        openedAtRef.current = Date.now();
        captureTerms("terms:modal_opened", {
            ...termsAnalyticsProperties,
            surface,
            trigger
        });
    }, [open, surface, trigger, captureTerms, termsAnalyticsProperties]);

    const handleAccept = (): void => {
        const startedAt = openedAtRef.current;
        captureTerms("terms:accepted", {
            ...termsAnalyticsProperties,
            surface,
            acceptance_latency_ms:
                startedAt !== null ? Date.now() - startedAt : undefined
        });
        onAccept();
        setTermsChecked(false);
    };

    const handleClose = (): void => {
        captureTerms("terms:rejected", {
            ...termsAnalyticsProperties,
            surface,
            had_checked_box: lastCheckedRef.current
        });
        onClose();
        setTermsChecked(false);
    };

    const handleCheckboxToggle = (next: boolean): void => {
        lastCheckedRef.current = next;
        captureTerms("terms:checkbox_toggled", {
            ...termsAnalyticsProperties,
            surface,
            checked: next
        });
        setTermsChecked(next);
    };

    if (surface === "retail_enrollment" && !document) return null;
    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen): void => {
                if (!nextOpen) handleClose();
            }}
        >
            <DialogContent className="w-full max-w-none md:max-w-xl max-h-[min(90vh,800px)] top-auto bottom-0 translate-y-0 md:top-1/2 md:bottom-auto md:-translate-y-1/2 overflow-hidden flex flex-col py-4 gap-0">
                <DialogTitle className="sr-only">
                    {document?.name ?? "Terms of Service"}
                </DialogTitle>
                <div className="flex justify-end px-4 shrink-0">
                    <DialogClose
                        render={
                            <ButtonIcon
                                variant="transparent"
                                size="xl"
                                aria-label="Close"
                                icon={<CrossIcon />}
                            />
                        }
                    />
                </div>

                <div className="px-4 pt-3 shrink-0">
                    <div className="h-px bg-line-base" />
                </div>

                <div
                    className="flex-1 overflow-y-scroll px-4 py-4"
                    style={{ scrollbarGutter: "stable" }}
                >
                    <SolomonTermsDocument
                        terms={
                            document
                                ? { ...document, text: document.content }
                                : solomonMainAppTerms
                        }
                    />

                    <div className="pt-4">
                        <button
                            type="button"
                            className="flex items-center gap-3 cursor-pointer bg-transparent border-none p-0"
                            onClick={(): void =>
                                handleCheckboxToggle(!termsChecked)
                            }
                        >
                            {termsChecked ? (
                                <span className="relative inline-flex shrink-0 text-accent">
                                    <CheckboxIcon className="size-6" />
                                    <CheckIcon className="absolute inset-0 m-auto size-3.5" />
                                </span>
                            ) : (
                                <span className="shrink-0 text-foreground-base">
                                    <CheckboxIcon className="size-6" />
                                </span>
                            )}
                            <Text
                                variant="body"
                                render={<span />}
                                className="text-foreground-base"
                            >
                                I accept the{" "}
                                {document?.name ?? "Terms of Service"}
                            </Text>
                        </button>
                    </div>

                    <Text
                        variant="small"
                        className="mt-4 text-foreground-neutral italic"
                    >
                        By accessing or using the Interface you affirm that you
                        have read, understood, and agree to the above Terms.
                    </Text>
                </div>

                <div className="px-4 pt-4 shrink-0 border-t border-line-base">
                    <Button
                        variant="raised"
                        size="lg"
                        interaction="lifted"
                        fullWidth
                        onClick={handleAccept}
                        disabled={!termsChecked}
                        trailing={<ArrowRightLongIcon className="w-6 h-6" />}
                        className="disabled:opacity-40 transition-opacity"
                    >
                        Accept and Continue
                    </Button>
                </div>

                <div className="h-safe-area-inset-bottom shrink-0" />
            </DialogContent>
        </Dialog>
    );
}

export default TermsOfServiceModal;
